/**
 * Beta Metrics Dashboard
 * 
 * Protected route displaying real-time beta program performance metrics
 * and success threshold monitoring for internal team use.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Activity,
  BarChart3,
  RefreshCw,
  Calendar,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MetricsSummary {
  period: string;
  library_readiness_success_rate: number;
  library_readiness_p95_seconds: number;
  analysis_p50_seconds: number;
  analysis_p95_seconds: number;
  telemetry_success_rate: number;
  error_rate: number;
  weekly_retention_rate: number;
  meets_targets: {
    telemetry_persistence: boolean;
    library_readiness: boolean;
    analysis_p50: boolean;
    analysis_p95: boolean;
    error_rate: boolean;
    retention: boolean;
  };
}

interface DailyStats {
  run_date: string;
  total_uploads: number;
  ready_percentage: number;
  readiness_p95_seconds: number;
  analysis_success_percentage: number;
  analysis_p50_seconds: number;
  analysis_p95_seconds: number;
  error_percentage: number;
  telemetry_success_percentage: number;
}

interface TelemetryStats {
  capture_date: string;
  total_attempts: number;
  successful_saves: number;
  success_percentage: number;
  avg_frame_count: number;
  avg_file_size_bytes: number;
  storage_failures: number;
  checksum_failures: number;
}

export function BetaMetricsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [metricsSummary, setMetricsSummary] = useState<MetricsSummary[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [telemetryStats, setTelemetryStats] = useState<TelemetryStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  /**
   * Check if user has access to beta metrics
   */
  const checkAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      navigate('/auth/login');
      return false;
    }

    // Check if user is admin or beta team member
    const userRole = user.user_metadata?.role;
    const isAuthorized = userRole === 'admin' || userRole === 'beta_team' || 
                        user.email?.endsWith('@nodoublesgolf.com');

    if (!isAuthorized) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to view beta metrics.',
        variant: 'destructive'
      });
      navigate('/');
      return false;
    }

    return true;
  };

  /**
   * Load all metrics data
   */
  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load metrics summary
      const { data: summaryData, error: summaryError } = await supabase
        .rpc('get_beta_metrics_summary');

      if (summaryError) throw summaryError;
      setMetricsSummary(summaryData || []);

      // Load daily stats (last 14 days)
      const { data: dailyData, error: dailyError } = await supabase
        .from('v_beta_run_stats')
        .select('*')
        .gte('run_date', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('run_date', { ascending: false });

      if (dailyError) throw dailyError;
      setDailyStats(dailyData || []);

      // Load telemetry stats (last 14 days)
      const { data: telemetryData, error: telemetryError } = await supabase
        .from('v_telemetry_success_rate')
        .select('*')
        .gte('capture_date', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
        .order('capture_date', { ascending: false });

      if (telemetryError) throw telemetryError;
      setTelemetryStats(telemetryData || []);

      setLastRefresh(new Date());
      
    } catch (err) {
      console.error('Failed to load metrics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
      toast({
        title: 'Error Loading Metrics',
        description: 'Failed to load beta metrics data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initialize page
   */
  useEffect(() => {
    const init = async () => {
      const hasAccess = await checkAccess();
      if (hasAccess) {
        await loadMetrics();
      }
    };

    init();
  }, []);

  /**
   * Auto-refresh every 5 minutes
   */
  useEffect(() => {
    const interval = setInterval(loadMetrics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  /**
   * Get metric status badge
   */
  const getMetricBadge = (value: number, target: number, isLowerBetter = false) => {
    const meets = isLowerBetter ? value < target : value > target;
    return (
      <Badge variant={meets ? 'default' : 'destructive'}>
        {meets ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
        {meets ? 'Target Met' : 'Below Target'}
      </Badge>
    );
  };

  /**
   * Format percentage
   */
  const formatPercentage = (value: number) => `${value?.toFixed(1) || 0}%`;

  /**
   * Format duration
   */
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  };

  if (loading && metricsSummary.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading beta metrics...</p>
        </div>
      </div>
    );
  }

  const todayMetrics = metricsSummary.find(m => m.period === 'today');
  const weekMetrics = metricsSummary.find(m => m.period === 'last_7_days');

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Beta Metrics Dashboard</h1>
              <p className="text-muted-foreground">
                Real-time monitoring of beta program success metrics
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <Calendar className="h-3 w-3 mr-1" />
                Last updated: {lastRefresh.toLocaleTimeString()}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={loadMetrics}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {error && (
            <Alert className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* Success Targets Overview */}
        {todayMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            
            {/* Telemetry Persistence */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Telemetry Persistence
                  {getMetricBadge(todayMetrics.telemetry_success_rate, 97)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(todayMetrics.telemetry_success_rate)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: &gt;97% | 7-day avg: {formatPercentage(weekMetrics?.telemetry_success_rate || 0)}
                </p>
              </CardContent>
            </Card>

            {/* Library Readiness */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Library Readiness P95
                  {getMetricBadge(todayMetrics.library_readiness_p95_seconds, 60, true)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(todayMetrics.library_readiness_p95_seconds)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: &lt;60s | 7-day avg: {formatDuration(weekMetrics?.library_readiness_p95_seconds || 0)}
                </p>
              </CardContent>
            </Card>

            {/* Analysis Latency P50 */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Analysis Latency P50
                  {getMetricBadge(todayMetrics.analysis_p50_seconds, 12, true)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(todayMetrics.analysis_p50_seconds)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: &lt;12s | 7-day avg: {formatDuration(weekMetrics?.analysis_p50_seconds || 0)}
                </p>
              </CardContent>
            </Card>

            {/* Analysis Latency P95 */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Analysis Latency P95
                  {getMetricBadge(todayMetrics.analysis_p95_seconds, 18, true)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatDuration(todayMetrics.analysis_p95_seconds)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: &lt;18s | 7-day avg: {formatDuration(weekMetrics?.analysis_p95_seconds || 0)}
                </p>
              </CardContent>
            </Card>

            {/* Error Rate */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Error Rate
                  {getMetricBadge(todayMetrics.error_rate, 1, true)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(todayMetrics.error_rate)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: &lt;1% | 7-day avg: {formatPercentage(weekMetrics?.error_rate || 0)}
                </p>
              </CardContent>
            </Card>

            {/* User Retention */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  Weekly Retention
                  {getMetricBadge(todayMetrics.weekly_retention_rate, 70)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatPercentage(todayMetrics.weekly_retention_rate)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Target: ≥70% | 2+ drills/week
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Metrics Tabs */}
        <Tabs defaultValue="daily" className="space-y-4">
          <TabsList>
            <TabsTrigger value="daily">Daily Performance</TabsTrigger>
            <TabsTrigger value="telemetry">Telemetry Details</TabsTrigger>
            <TabsTrigger value="targets">Target Analysis</TabsTrigger>
          </TabsList>

          {/* Daily Performance Tab */}
          <TabsContent value="daily">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Daily Performance Trends (Last 14 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-right p-2">Uploads</th>
                        <th className="text-right p-2">Ready %</th>
                        <th className="text-right p-2">Ready P95</th>
                        <th className="text-right p-2">Analysis %</th>
                        <th className="text-right p-2">Analysis P50</th>
                        <th className="text-right p-2">Analysis P95</th>
                        <th className="text-right p-2">Error %</th>
                        <th className="text-right p-2">Telemetry %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyStats.map((stat) => (
                        <tr key={stat.run_date} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-medium">
                            {new Date(stat.run_date).toLocaleDateString()}
                          </td>
                          <td className="text-right p-2">{stat.total_uploads}</td>
                          <td className="text-right p-2">{formatPercentage(stat.ready_percentage)}</td>
                          <td className="text-right p-2">{formatDuration(stat.readiness_p95_seconds)}</td>
                          <td className="text-right p-2">{formatPercentage(stat.analysis_success_percentage)}</td>
                          <td className="text-right p-2">{formatDuration(stat.analysis_p50_seconds)}</td>
                          <td className="text-right p-2">{formatDuration(stat.analysis_p95_seconds)}</td>
                          <td className="text-right p-2">
                            <span className={stat.error_percentage > 1 ? 'text-destructive font-medium' : ''}>
                              {formatPercentage(stat.error_percentage)}
                            </span>
                          </td>
                          <td className="text-right p-2">
                            <span className={stat.telemetry_success_percentage < 97 ? 'text-destructive font-medium' : ''}>
                              {formatPercentage(stat.telemetry_success_percentage)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Telemetry Details Tab */}
          <TabsContent value="telemetry">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Telemetry Persistence Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Date</th>
                        <th className="text-right p-2">Attempts</th>
                        <th className="text-right p-2">Success</th>
                        <th className="text-right p-2">Success %</th>
                        <th className="text-right p-2">Avg Frames</th>
                        <th className="text-right p-2">Avg Size</th>
                        <th className="text-right p-2">Storage Fails</th>
                        <th className="text-right p-2">Checksum Fails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {telemetryStats.map((stat) => (
                        <tr key={stat.capture_date} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-medium">
                            {new Date(stat.capture_date).toLocaleDateString()}
                          </td>
                          <td className="text-right p-2">{stat.total_attempts}</td>
                          <td className="text-right p-2">{stat.successful_saves}</td>
                          <td className="text-right p-2">
                            <span className={stat.success_percentage < 97 ? 'text-destructive font-medium' : ''}>
                              {formatPercentage(stat.success_percentage)}
                            </span>
                          </td>
                          <td className="text-right p-2">{Math.round(stat.avg_frame_count)}</td>
                          <td className="text-right p-2">{Math.round(stat.avg_file_size_bytes / 1024)}KB</td>
                          <td className="text-right p-2">
                            <span className={stat.storage_failures > 0 ? 'text-destructive font-medium' : ''}>
                              {stat.storage_failures}
                            </span>
                          </td>
                          <td className="text-right p-2">
                            <span className={stat.checksum_failures > 0 ? 'text-destructive font-medium' : ''}>
                              {stat.checksum_failures}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Target Analysis Tab */}
          <TabsContent value="targets">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Success Targets */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Success Targets
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {todayMetrics && Object.entries(todayMetrics.meets_targets).map(([key, meets]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <Badge variant={meets ? 'default' : 'destructive'}>
                        {meets ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                        {meets ? 'Met' : 'Not Met'}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Performance Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Performance Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm space-y-2">
                    <p>
                      <strong>Overall Health:</strong>{' '}
                      {todayMetrics && Object.values(todayMetrics.meets_targets).filter(Boolean).length >= 4 ? (
                        <span className="text-green-600">Good</span>
                      ) : (
                        <span className="text-red-600">Needs Attention</span>
                      )}
                    </p>
                    <p>
                      <strong>Targets Met:</strong>{' '}
                      {todayMetrics ? Object.values(todayMetrics.meets_targets).filter(Boolean).length : 0} / 6
                    </p>
                    <p>
                      <strong>Critical Issues:</strong>{' '}
                      {todayMetrics && (
                        todayMetrics.error_rate > 2 || 
                        todayMetrics.telemetry_success_rate < 95 ||
                        todayMetrics.analysis_p95_seconds > 25
                      ) ? (
                        <span className="text-red-600">Yes</span>
                      ) : (
                        <span className="text-green-600">None</span>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default BetaMetricsPage;
